import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { FormikProvider, useFormik } from 'formik';
import { useNavigate } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';

import { CREATE_USER } from '../../http/apollo-queries';

import { Button } from '../../components/UI';
import { InputCode } from '../../components/form';
import { useAuth } from '../../context/Auth';
import { useSignUpContext } from './SignUpContext';
import { Modal } from '../../components/Modal';

import { trackEvent } from '../../helpers/analytics';
import { logger } from '../../helpers/logger';

const SignUpConfirmEmail = () => {
  const { t, i18n } = useTranslation();
  const { confirmAccount, signIn, resendCode, refresh, refetch } = useAuth();
  const { state, dispatch } = useSignUpContext();

  const navigate = useNavigate();
  const [createUserMutation] = useMutation(CREATE_USER);
  const [isLoading, setIsLoading] = useState(false);
  const [verifyEmailErr, setVerifyEmailErr] = useState('');
  const [isVerificationEmailResent, setIsVerificationEmailResent] =
    useState(false);

  const { email, password, referralCode } = state;

  const formik = useFormik({
    initialValues: {
      code: '',
    },
    onSubmit: (values) => {
      setIsLoading(true);

      trackEvent('signup_2fa_code');

      confirmAccount(email, values.code)
        .then((res) => {
          //after confirmation signIn with stored credentials
          return signIn(email, password);
        })
        .then((res) => {
          //create userMeta on db
          return createUserMutation({
            variables: {
              language: i18n.language.toUpperCase(),
              referralCode: referralCode,
            },
          });
        })
        .then((res) => {
          //needed to get a new token including cognito groups previous added
          return refresh();
        })
        .then((res) => {
          //refetch user to update user status
          return refetch();
        })
        .then((res) => {
          //clear stored credentials and proceed
          dispatch({
            type: 'CLEAR',
            payload: '',
          });

          navigate('/choose-username');
        })
        .catch((err) => {
          logger.error('verifyEmail - error', err);
          setIsLoading(false);
          setVerifyEmailErr(
            t(`verifyEmail.form.error.${err.code}`, {
              defaultValue: t('verifyEmail.form.error.general'),
            })
          );
        });
    },
    validateOnMount: true,
  });

  const resendEmail = () => {
    trackEvent('signup_2fa_email');

    resendCode(email!).then(() => {
      setIsVerificationEmailResent(true);
      setTimeout(() => {
        setIsVerificationEmailResent(false);
      }, 30000);
    });
  };

  return (
    <Modal
      isOpen={true}
      backdrop={'solid'}
      handleMerlinLogo={true}
      isClosable={false}
      padding={'huge'}
    >
      <div className={'text-center'}>
        <h1
          className={
            'text-black dark:text-white text-xl font-semibold text-center mb-4'
          }
        >
          {t('verifyEmail.title')}
        </h1>

        <p className={'text-grey-50 text-sm'}>
          <Trans i18nKey="verifyEmail.subtitle" email={email}>
            email sent to
            <span className="text-black font-semibold dark:text-white">
              {{ email }}
            </span>
            insert code
          </Trans>
        </p>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <div className="mt-12">
              <InputCode
                name="code"
                id="code"
                numberOfDigits={6}
                formik={formik}
                label={t('verifyEmail.form.label.verificationCode')}
                errorMessage={verifyEmailErr}
              />
            </div>
            <div className="mt-10">
              <Button
                type="submit"
                variant="primary"
                buttonClass="w-64"
                loading={isLoading}
              >
                {t('verifyEmail.continue')}
              </Button>
            </div>
          </form>
        </FormikProvider>
        <div className="mt-5">
          <span className="text-grey-80">
            {t(
              !isVerificationEmailResent
                ? 'verifyEmail.notReceived'
                : 'verifyEmail.resent'
            )}
          </span>
          <button
            className="text-sm text-black font-semibold"
            onClick={() => resendEmail()}
          >
            {!isVerificationEmailResent && t('verifyEmail.sendNew')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SignUpConfirmEmail;
